import React, { useState, useEffect } from 'react';
import api from '../api';

var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatMonth(monthStr) {
  if (!monthStr) return '';
  var parts = monthStr.split('-');
  return MONTHS[parseInt(parts[1], 10) - 1] + ' ' + parts[0];
}

function StatItem(props) {
  return (
    <div className="pr-stat-item">
      <div className="pr-stat-label">{props.label}</div>
      <div className={'pr-stat-value' + (props.color ? ' ' + props.color : '')}>
        {props.value}
      </div>
    </div>
  );
}

function ProgressBar(props) {
  var pct = Math.min(Math.max(props.pct || 0, 0), 100);
  return (
    <div className="pr-progress-wrap">
      <div className={'pr-progress-fill ' + (props.color || 'teal')} style={{ width: pct + '%' }} />
    </div>
  );
}

function formatHours(minutes) {
  if (!minutes) return '0h';
  var h = Math.floor(minutes / 60);
  var m = minutes % 60;
  return m > 0 ? h + 'h ' + m + 'm' : h + 'h';
}

export default function ActivityTab(props) {
  var selectedUser = props.selectedUser;
  var selectedMonth = props.selectedMonth;
  var prevMonth = props.prevMonth;
  var nextMonth = props.nextMonth;
  var getUserId = props.getUserId;

  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(false);

  useEffect(function() {
    if (!selectedUser) return;
    var userId = getUserId(selectedUser);
    if (!userId) return;
    setLoading(true);
    api.get(process.env.REACT_APP_URL + '/payroll/activity-summary/' + userId + '?month=' + selectedMonth)
      .then(function(res) {
        if (res && res.data && res.data.success) setData(res.data);
        else setData(null);
      })
      .catch(function() { setData(null); })
      .finally(function() { setLoading(false); });
  }, [selectedUser, selectedMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!selectedUser) return null;

  var att = (data && data.attendance) || {};
  var sess = (data && data.sessions) || {};
  var tasks = (data && data.tasks) || {};
  var checklists = (data && data.checklists) || [];
  var clSummary = (data && data.checklistSummary) || {};

  var attendancePct = att.workedDays ? Math.round((att.workedDays / 26) * 100) : 0;
  var sessionCompletionPct = sess.total > 0 ? Math.round((sess.completed / sess.total) * 100) : 0;
  var taskTotal = (tasks.completed || 0) + (tasks.pending || 0);
  var taskCompletionPct = taskTotal > 0 ? Math.round(((tasks.completed || 0) / taskTotal) * 100) : 0;
  var meritScore = (tasks.greenStars || 0) - (tasks.redStars || 0);

  return (
    <div>
      {/* Month Navigator */}
      <div className="pr-month-nav">
        <button className="pr-month-btn" onClick={prevMonth}>&larr;</button>
        <span className="pr-month-label">{formatMonth(selectedMonth)}</span>
        <button className="pr-month-btn" onClick={nextMonth}>&rarr;</button>
      </div>

      {loading && <div className="pr-loading">Loading activity data...</div>}

      {!loading && !data && (
        <div className="pr-card" style={{ textAlign: 'center', color: '#636e72', padding: 40 }}>
          No activity data available for this month
        </div>
      )}

      {!loading && data && (
        <>
          {/* ── Attendance Overview ── */}
          <div className="pr-activity-card attendance">
            <div className="pr-activity-title">Attendance Overview</div>
            <div className="pr-stat-row">
              <StatItem label="Worked" value={att.workedDays || 0} color="blue" />
              <StatItem label="Absents" value={att.absents || 0} color={att.absents > 0 ? 'red' : ''} />
              <StatItem label="Leaves" value={att.leaves || 0} />
              <StatItem label="Half Days" value={att.workedHalfDays || 0} />
              <StatItem label="Sundays" value={att.workedSundays || 0} color="violet" />
              <StatItem label="Hours" value={formatHours(att.totalWorkMinutes)} />
            </div>
            <ProgressBar pct={attendancePct} color="blue" />
            <div style={{ fontSize: '0.72rem', color: '#636e72', marginTop: 4, textAlign: 'right' }}>
              {att.workedDays || 0} / 26 days ({attendancePct}%)
            </div>
          </div>

          {/* ── Patient Sessions ── */}
          <div className="pr-activity-card sessions">
            <div className="pr-activity-title">Patient Sessions</div>
            <div className="pr-stat-row">
              <StatItem label="Total" value={sess.total || 0} />
              <StatItem label="Completed" value={sess.completed || 0} color="green" />
              <StatItem label="Skipped" value={sess.skipped || 0} color={sess.skipped > 0 ? 'red' : ''} />
              <StatItem label="Patients" value={sess.uniquePatients || 0} color="violet" />
              <StatItem label="Avg/Day" value={sess.avgPerDay || 0} color="blue" />
            </div>
            <ProgressBar pct={sessionCompletionPct} color="violet" />
            <div style={{ fontSize: '0.72rem', color: '#636e72', marginTop: 4, textAlign: 'right' }}>
              {sess.completed || 0} / {sess.total || 0} completed ({sessionCompletionPct}%)
            </div>
          </div>

          {/* ── Task Performance ── */}
          <div className="pr-activity-card tasks">
            <div className="pr-activity-title">Task Performance</div>
            <div className="pr-stat-row">
              <StatItem label="Completed" value={tasks.completed || 0} color="green" />
              <StatItem label="Pending" value={tasks.pending || 0} />
              <StatItem label="Late" value={tasks.late || 0} color={tasks.late > 0 ? 'red' : ''} />
              <StatItem label="Green Stars" value={tasks.greenStars || 0} color="green" />
              <StatItem label="Red Stars" value={tasks.redStars || 0} color={tasks.redStars > 0 ? 'red' : ''} />
              <StatItem label="Merit" value={meritScore >= 0 ? '+' + meritScore : meritScore} color={meritScore >= 0 ? 'green' : 'red'} />
            </div>
            <ProgressBar pct={taskCompletionPct} color="teal" />
            <div style={{ fontSize: '0.72rem', color: '#636e72', marginTop: 4, textAlign: 'right' }}>
              {tasks.completed || 0} / {taskTotal} completed ({taskCompletionPct}%)
            </div>
          </div>

          {/* ── Checklist Compliance ── */}
          <div className="pr-activity-card checklists">
            <div className="pr-activity-title">Checklist Compliance</div>
            <div className="pr-stat-row">
              <StatItem label="Total Due" value={clSummary.totalDue || 0} />
              <StatItem label="On Time" value={clSummary.completedOnTime || 0} color="green" />
              <StatItem label="Late" value={clSummary.completedLate || 0} color={clSummary.completedLate > 0 ? 'red' : ''} />
              <StatItem label="Missed" value={clSummary.notCompleted || 0} color={clSummary.notCompleted > 0 ? 'red' : ''} />
              <StatItem label="Score" value={(clSummary.compliancePct || 0) + '%'} color={clSummary.compliancePct >= 80 ? 'green' : 'red'} />
            </div>
            <ProgressBar pct={clSummary.compliancePct || 0} color={clSummary.compliancePct >= 80 ? 'teal' : 'coral'} />

            {/* Per-checklist breakdown */}
            {checklists.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#636e72', marginBottom: 8 }}>
                  Breakdown
                </div>
                {checklists.map(function(cl, i) {
                  if (!cl.total) return null;
                  return (
                    <div className="pr-checklist-row" key={i}>
                      <div className="pr-checklist-title">{cl.title || 'Checklist ' + (i + 1)}</div>
                      <div className="pr-checklist-counts">
                        {cl.completedOnTime > 0 && <span className="pr-cl-chip pr-cl-ontime">{cl.completedOnTime}</span>}
                        {cl.completedLate > 0 && <span className="pr-cl-chip pr-cl-late">{cl.completedLate}</span>}
                        {cl.notCompleted > 0 && <span className="pr-cl-chip pr-cl-missed">{cl.notCompleted}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
